import { LoadingStageModel } from '@kts-front/models';
import { BaseResponse } from '@kts-front/types';
import { action, computed, IReactionDisposer, makeObservable, observable, reaction, runInAction } from 'mobx';

import { ENDPOINTS } from 'configs/endpoints';
import { logApiErrWithSentry, logWithSentry, SentryErrorKind } from 'scripts/initSentry';
import { apiStore } from 'stores/global/ApiStore';
import { OpenStore } from 'stores/local/shared/OpenStore';
import { captureException } from 'utils/captureException';

import { UserStore } from '../UserStore';

import { urlBase64ToUint8Array } from './utils';

type Params = {
  userStore: UserStore;
};

type PrivateFields = '_registration' | '_subscription' | '_setSubscription' | '_start' | '_subscribe';

const OPEN_DELAY = 1000;

export class PushSubscribeStore {
  readonly modal = new OpenStore();

  private readonly _publicKeyRequest = apiStore.createRequest({
    method: 'GET',
    url: ENDPOINTS.webPush.getPublicKey,
  });

  private readonly _subscribeRequest = apiStore.createRequest({
    method: 'POST',
    url: ENDPOINTS.webPush.subscribe,
  });

  private readonly _unsubscribeRequest = apiStore.createRequest({
    method: 'DELETE',
    url: ENDPOINTS.webPush.unsubscribe,
  });

  private _registration: ServiceWorkerRegistration | null = null;
  private _subscription: PushSubscription | null = null;
  readonly loadingStage = new LoadingStageModel();

  private readonly _userStore: Params['userStore'];
  private readonly _onLogout: IReactionDisposer;

  constructor({ userStore }: Params) {
    this._userStore = userStore;

    makeObservable<this, PrivateFields>(this, {
      initSubscribe: action.bound,
      _registration: observable.ref,
      _subscription: observable,
      _start: action,
      _setSubscription: action,
      _subscribe: action,
      canSubscribe: computed,
    });

    this._start();

    this._onLogout = reaction(
      () => userStore.loadingStageLogout.isSuccess,
      () => {
        this.unsubscribe();
      },
    );
  }

  get canSubscribe(): boolean {
    return Boolean(
      this._userStore.user &&
      this._registration &&
      !this._subscription &&
      !this._userStore.loadingStageLogout.isLoading,
    );
  }

  get canUsePushes(): boolean {
    return (
      'Notification' in window &&
      Notification.permission !== 'denied' &&
      'serviceWorker' in navigator &&
      'PushManager' in window
    );
  }

  initSubscribe(): void {
    if (!this.canSubscribe) {
      return;
    }

    if (Notification.permission === 'granted') {
      this._subscribe();

      return;
    }

    setTimeout(() => this.modal.open(), OPEN_DELAY);
  }

  trySubscribe = async (): Promise<void> => {
    const result = await Notification.requestPermission();

    this.modal.close();

    if (result === 'granted') {
      this._subscribe();
    }
  };

  async unsubscribe(): Promise<void> {
    if (!this._subscription) {
      return;
    }

    await this._subscription.unsubscribe();
    await this._unsubscribeRequest.call({
      data: {
        endpoint: this._subscription.endpoint,
      },
    });
    this._setSubscription(null);
  }

  private async _start(): Promise<void> {
    if (!this.canUsePushes) {
      return;
    }

    const registration = await navigator.serviceWorker.ready;

    await runInAction(async () => {
      this._registration = registration;

      const subscription = await registration.pushManager.getSubscription();

      this._setSubscription(subscription);
    });
  }

  private async _serverSubscribe(subscription: PushSubscription | null): Promise<BaseResponse> {
    if (!subscription) {
      captureException(new Error('subscription is null'), 'error');

      return { isError: true };
    }

    const keys = subscription.toJSON().keys;

    if (!keys?.auth || !keys?.p256dh) {
      captureException(new Error('subscription does not have keys'), 'error', {
        subscription,
        keys,
      });

      return { isError: true };
    }

    const requestPayload = {
      endpoint: subscription.endpoint,
      auth: keys.auth,
      p256dh: keys.p256dh,
    };

    const response = await this._subscribeRequest.call({
      data: requestPayload,
    });
    const isError = response.isError;

    if (isError) {
      logApiErrWithSentry({
        endpoint: subscription.endpoint,
        method: 'POST',
        requestPayload,
        response,
      });
      captureException(new Error('error while making request to server'), 'error', {
        subscription,
        keys,
      });
    }

    return { isError };
  }

  private async _subscribe(): Promise<void> {
    if (!this._registration || this.loadingStage.isLoading) {
      return;
    }

    try {
      this.loadingStage.loading();
      const response = await this._publicKeyRequest.call<string>();

      if (response.isError) {
        this.loadingStage.error();

        logApiErrWithSentry({
          endpoint: 'subscribe error',
          method: 'GET',
          requestPayload: {},
          response,
        });

        return;
      }

      const applicationServerKey = urlBase64ToUint8Array(response.data);
      const newSubscription = await this._registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      const { isError: isErrorSubscribe } = await this._serverSubscribe(newSubscription);

      if (isErrorSubscribe) {
        this.loadingStage.error();
        newSubscription.unsubscribe();
      } else {
        this._setSubscription(newSubscription);
        this.loadingStage.success();
      }
    } catch (error) {
      this.loadingStage.error();
      logWithSentry(error, SentryErrorKind.other);
      captureException(error, 'subscribe error');
    }
  }

  private _setSubscription(subscription: PushSubscription | null): void {
    this._subscription = subscription;
  }
}
